/* eslint-disable camelcase */
import { getSystemOfUnits } from '../../../util/user';
import getMetaByApp from './getMetaByApp';

const TO_FEET_MULTIPLIER = 3.28084;

const convertToFeet = (val, unitSystem) => {
	if (unitSystem === 'Imperial' && val) {
		return val * TO_FEET_MULTIPLIER;
	}
	return val;
}
// Synthetic decode table for ROW landusestate.
// Index = numeric code written by the backend ROW serializer.
// Codes intentionally mirror the Y-axis positions in the frontend LAND_STATE_PROPS
// so the same ordering is shared on both sides.
// See: AIP-Backend/packages/aip-scanline/Plan.For.SurveyType.ROW.md
const LAND_STATE_BY_CODE = [
	undefined,         // 0 — unknown / no state
	'Forest',          // 1
	'Swamp',           // 2
	'Marsh',           // 3
	'Wetland',         // 4
	'Pasture',         // 5
	'Cropland',        // 6
	'Manicured',       // 7
	'NativeGrassland', // 8
	'Transportation',  // 9
	'Clay',            // 10
	'Silt',            // 11
	'Sand',            // 12
	'Gravel',          // 13
	'Cobble',          // 14
	'Rocky',           // 15
	'Brush',           // 16
];

// Enrichment map for ROW: derives the specific Falcon alias label from the
// numeric rowaliasidlist code already present in the binary buffer.
// Source: SurveyType.ROW.md — ROW Alias Catalog (© 2020 PureHM Inc.)
// Three codes confirmed in sample data: 22, 43, 110.
const ALIAS_ID_TO_LABEL = {
	// Signs (1–10)
	1: 'Sign',                       2: 'Pipeline Marker',
	3: 'Aerial Marker',              4: 'Foreign Line Marker',
	5: 'Abandoned Pipeline Marker',  6: 'GPS Control Marker',
	7: 'Milepost',                   8: 'Traffic Sign',
	9: 'ROW Boundary Post',          10: 'Test Post',
	// Fences (20–29)
	20: 'Fence',                     21: 'Barbed Wire Fence',
	22: 'Chain Link Fence',          23: 'Wood Fence',
	24: 'Pipe Fence',                25: 'Masonry Fence',
	26: 'Electric Fence',            27: 'Game Fence',
	28: 'Snow Fence',                29: 'Fence Post',
	// Roads (40–49)
	40: 'Road',                      41: 'Pedestrian Path',
	42: 'Driveway',                  43: '2-Track or Trail',
	44: 'Gravel Road',               45: 'Paved Road',
	46: 'Primary Highway',           47: 'Secondary Highway',
	48: 'Railroad Tracks',           49: 'Bridge',
	// Flowing Water (60–65)
	60: 'Flowing Water',             61: 'Run-Off',
	62: 'Creek',                     63: 'River',
	64: 'Channel',                   65: 'Irrigation Ditch',
	// Standing Water (80–85)
	80: 'Standing Water',            81: 'Run-Off',
	82: 'Pond',                      83: 'Lake',
	84: 'Swamp or Slough',           85: 'Marsh',
	// Pipe or Cable Crossings (100–104)
	100: 'Pipe or Cable Crossing',   101: 'Overhead Cables',
	102: 'Underground Cables',       103: 'Above Ground Pipe',
	104: 'Below Ground Pipe',
	// Ground Entry or Exit (110–112)
	110: 'Ground Entry or Exit',     111: 'Riser',
	112: 'Span',
	// Hills and Berms (120–124)
	120: 'Hill',                     121: 'Base of Hill',
	122: 'Crest of Hill',            123: 'Base of Berm',
	124: 'Crest of Berm',
	// Holes and Ditches (130–135)
	130: 'Hole',                     131: 'Sinkhole or Settled Ground',
	132: 'Construction Excavation',  133: 'Ditch',
	134: 'Roadside Ditch',           135: 'Drainage Ditch',
	// Structures (150–156)
	150: 'Structure',                151: 'Pipeline Control Building',
	152: 'Foreign Building',         153: 'Metering Building',
	154: 'Power Pole',               155: 'Anchor',
	156: 'Vault',
};

const mapToBASE = (view, index, parsers, unitSystem) => {
	const {
		getId,
		getSurveyType,
		getLongitude,
		getLattitude,
		getDateCreated,
		getGpsDistance,
	} = parsers;
	return {
		id: getId(view, index),
		type: getSurveyType(view, index),
		loc: [getLongitude(view, index), getLattitude(view, index)],
		date: getDateCreated(view, index),
		gpsdistance: convertToFeet(getGpsDistance(view, index), unitSystem)
	};
};

const getMapTos = (appName, unitSystem) => {
	const { parsers } = getMetaByApp(appName);

	const { getReadingValue1, getReadingValue2, getReadingValue3, getReadingValue4, getReadingValue5, getAliasList } = parsers;

	return {
		ON_OFF: (view, index) => ({
			...mapToBASE(view, index, parsers, unitSystem),
			on: getReadingValue1(view, index) / 1000,
			off: getReadingValue2(view, index) / 1000
		}),

		'ON-OFF': (view, index) => ({
			...mapToBASE(view, index, parsers, unitSystem),
			on: getReadingValue1(view, index) / 1000,
			off: getReadingValue2(view, index) / 1000
		}),

		ON: (view, index) => ({
			...mapToBASE(view, index, parsers, unitSystem),
			on: getReadingValue1(view, index) / 1000
		}),

		DOC: (view, index) => {
			const item = {
				...mapToBASE(view, index, parsers, unitSystem),
				doc: convertToFeet(getReadingValue1(view, index), unitSystem) / 100 || undefined,
			};
			return item;
		},

		REL: (view, index) => ({
			...mapToBASE(view, index, parsers, unitSystem),
			gRate: getReadingValue1(view, index),
			dep: getReadingValue2(view, index)
		}),

		DEPOL: (view, index) => ({
			...mapToBASE(view, index, parsers, unitSystem),
			on: getReadingValue1(view, index) / 1000
		}),

		NATIVE: (view, index) => ({
			...mapToBASE(view, index, parsers, unitSystem),
			on: getReadingValue1(view, index) / 1000
		}),

		DCVG: (view, index) => {
			const DCVG_NUM_REP_UNDEFINED = -999;
			const ir_pct = getReadingValue1(view, index) / 1000;
			return {
				...mapToBASE(view, index, parsers, unitSystem),
				ir_pct: ir_pct === DCVG_NUM_REP_UNDEFINED ? undefined : ir_pct
			};
		},

		ACVG: (view, index) => ({
			...mapToBASE(view, index, parsers, unitSystem),
			decmicrov: getReadingValue1(view, index) / 100,
			normdecmicrov: getReadingValue2(view, index) / 100,
			initialcurrent: getReadingValue3(view, index) / 100,
		}),

		ACCA: (view, index) => ({
			...mapToBASE(view, index, parsers, unitSystem),
			current: getReadingValue1(view, index) / 1000,
			attenuation: getReadingValue2(view, index) / 1000,
		}),

		ACPSP: (view, index) => ({
			...mapToBASE(view, index, parsers, unitSystem),
			ac: getReadingValue1(view, index) / 1000,
		}),

		PROFILE: (view, index) => ({
			...mapToBASE(view, index, parsers, unitSystem),
			elevation: convertToFeet(getReadingValue1(view, index), unitSystem) / 1000,
			topelv: convertToFeet(getReadingValue2(view, index), unitSystem) / 1000,
			copelv: convertToFeet(getReadingValue3(view, index), unitSystem) / 1000,
			bopelv: convertToFeet(getReadingValue4(view, index), unitSystem) / 1000,
			wtrelv: convertToFeet(getReadingValue5(view, index), unitSystem) / 1000,
		}),

		REPORTED_ANOMALIES: (view, index) => ({
			...mapToBASE(view, index, parsers, unitSystem),
			aliaslist: getAliasList(view, index),
		}),

		ROW: (view, index) => {
			const rowaliasidlist = getReadingValue1(view, index);
			return {
				...mapToBASE(view, index, parsers, unitSystem),
				rowaliasidlist,
				rowaliaslist: ALIAS_ID_TO_LABEL[rowaliasidlist] || undefined,
				landusestate: LAND_STATE_BY_CODE[getReadingValue2(view, index)],
			};
		},
	};
};

export const getReadingMapper = (surveyType, appName, unitSystem = getSystemOfUnits()) => {
	const MAP_TO = getMapTos(appName, unitSystem);
	return MAP_TO[surveyType] || MAP_TO.ON_OFF;
};

export default {
	getReadingMapper
};

// @note-supported-constant-needs-abstraction
